import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { Combobox } from '@cbhq/cds-web/alpha/combobox';
import type {
  SelectControlComponent,
  SelectControlProps,
  SelectOptionComponent,
  SelectOptionProps,
} from '@cbhq/cds-web/alpha/select';
import { DefaultSelectControl, DefaultSelectOption } from '@cbhq/cds-web/alpha/select';
import { SelectChip } from '@cbhq/cds-web/alpha/select-chip/SelectChip';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Box } from '@cbhq/cds-web/layout/Box';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { Pressable } from '@cbhq/cds-web/system/Pressable';
import { Text } from '@cbhq/cds-web/typography/Text';
import { defineMessages, useIntl } from '@cbhq/i18n';

import { workspaceSelectChipOverrides } from '../../../components/selectStyles';

const messages = defineMessages({
  searchPlaceholder: {
    id: 'activityFilters.searchPlaceholder',
    defaultMessage: 'Search',
    description: 'Placeholder text for the activity audit log email search input',
  },
  searchAccessibilityLabel: {
    id: 'activityFilters.searchAccessibilityLabel',
    defaultMessage: 'Search activity',
    description: 'Accessibility label for the activity audit log email search input',
  },
  noEmailResults: {
    id: 'activityFilters.noEmailResults',
    defaultMessage: 'No results found',
    description: 'Message shown when no results match the activity search',
  },
  actionType: {
    id: 'activityFilters.actionType',
    defaultMessage: 'Action type',
    description: 'Default label for the action type filter chip',
  },
  date: {
    id: 'activityFilters.date',
    defaultMessage: 'Date',
    description: 'Default label for the date filter chip',
  },
  anyDate: {
    id: 'activityFilters.anyDate',
    defaultMessage: 'Any date',
    description: 'Option label for clearing the date filter',
  },
  clearAll: {
    id: 'activityFilters.clearAll',
    defaultMessage: 'Clear all',
    description: 'Label for the clear-all button in multi-select filter chips',
  },
  filterByActionType: {
    id: 'activityFilters.filterByActionType',
    defaultMessage: 'Filter by action type',
    description: 'Accessibility label for the action type filter chip',
  },
  filterByDate: {
    id: 'activityFilters.filterByDate',
    defaultMessage: 'Filter by date',
    description: 'Accessibility label for the date filter chip',
  },
  clearFilters: {
    id: 'activityFilters.clearFilters',
    defaultMessage: 'Clear filters',
    description: 'Label for the pressable that clears all active activity filters',
  },
});

const hiddenScrollbarClassNames = workspaceSelectChipOverrides.classNames;

const emailComboboxOverrides = {
  classNames: hiddenScrollbarClassNames,
  styles: {
    root: {
      width: '100%',
    },
    optionContent: {
      minWidth: 0,
      paddingBottom: 4,
      paddingTop: 4,
      width: '100%',
    },
    optionLabel: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '100%',
    },
    controlEndNode: {
      display: 'none',
    },
  },
} as const;

const clearFiltersStyle = { cursor: 'pointer', whiteSpace: 'nowrap' } as const;
const searchIcon = <Icon name="search" size="s" color="fgMuted" />;

const EmailSearchControl = forwardRef<HTMLDivElement, SelectControlProps<'single', string>>(
  function EmailSearchControl(props, ref) {
    return <DefaultSelectControl {...props} ref={ref} borderRadius={1000} />;
  },
) as SelectControlComponent<'single', string>;

const EmailSearchOption = forwardRef<HTMLButtonElement, SelectOptionProps<'single', string>>(
  function EmailSearchOption(props, ref) {
    return <DefaultSelectOption {...props} ref={ref} media={undefined} />;
  },
) as SelectOptionComponent<'single', string>;

export type ActivityFilterOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type ActivityFiltersProps = {
  emailFilter: string;
  onEmailFilterChange: (value: string) => void;
  emailOptions: ActivityFilterOption[];
  emailDropdownDefaultOpen?: boolean;
  hasActorFilter: boolean;
  actionTypeFilter: string[];
  onActionTypeFilterChange: (value: string | string[] | null) => void;
  actionTypeOptions: ActivityFilterOption[];
  dateFilter: string | null;
  onDateFilterChange: (value: string | null) => void;
  dateOptions: ActivityFilterOption[];
  onClearFilters: () => void;
};

export function ActivityFilters({
  emailFilter,
  onEmailFilterChange,
  emailOptions,
  emailDropdownDefaultOpen,
  hasActorFilter,
  actionTypeFilter,
  onActionTypeFilterChange,
  actionTypeOptions,
  dateFilter,
  onDateFilterChange,
  dateOptions,
  onClearFilters,
}: ActivityFiltersProps) {
  const { formatMessage } = useIntl();
  const [emailSearchText, setEmailSearchText] = useState(emailFilter);

  // Sync the local search text to emailFilter during render (rather than in an
  // effect) so a parent-driven reset applies in a single render pass instead of
  // rendering once with stale text and re-rendering after the effect fires.
  const prevEmailFilter = useRef(emailFilter);
  if (prevEmailFilter.current !== emailFilter) {
    prevEmailFilter.current = emailFilter;
    setEmailSearchText(emailFilter);
  }

  const renderedDateOptions = useMemo(
    () => [{ value: null, label: formatMessage(messages.anyDate) }, ...dateOptions],
    [dateOptions, formatMessage],
  );
  const clearAllLabel = formatMessage(messages.clearAll);

  const handleEmailSearch = useCallback((value: string) => {
    setEmailSearchText(value);
  }, []);

  const handleEmailChange = useCallback(
    (value: string | null) => {
      if (!value) {
        return;
      }

      setEmailSearchText(value);
      onEmailFilterChange(value);
    },
    [onEmailFilterChange],
  );

  const handleClearFilters = useCallback(() => {
    setEmailSearchText('');
    onClearFilters();
  }, [onClearFilters]);

  const hasActiveFilters =
    hasActorFilter ||
    actionTypeFilter.length > 0 ||
    dateFilter !== null ||
    emailSearchText.length > 0;

  return (
    <HStack gap={1.5} alignItems="center" flexWrap="nowrap">
      <Box flexShrink={1} minWidth={300} maxWidth={500} width="100%">
        <Combobox<'single', string>
          {...emailComboboxOverrides}
          type="single"
          value={null}
          onChange={handleEmailChange}
          searchText={emailSearchText}
          onSearch={handleEmailSearch}
          defaultOpen={emailDropdownDefaultOpen}
          options={emailOptions}
          placeholder={formatMessage(messages.searchPlaceholder)}
          controlAccessibilityLabel={formatMessage(messages.searchAccessibilityLabel)}
          emptyOptionsLabel={formatMessage(messages.noEmailResults)}
          SelectControlComponent={EmailSearchControl}
          SelectOptionComponent={EmailSearchOption}
          startNode={searchIcon}
          variant="secondary"
          bordered={false}
          compact
        />
      </Box>

      <Box flexShrink={0}>
        <SelectChip<'multi', string>
          type="multi"
          {...workspaceSelectChipOverrides}
          value={actionTypeFilter}
          onChange={onActionTypeFilterChange}
          label={formatMessage(messages.actionType)}
          controlAccessibilityLabel={formatMessage(messages.filterByActionType)}
          options={actionTypeOptions}
          clearAllLabel={clearAllLabel}
        />
      </Box>

      <Box flexShrink={0}>
        <SelectChip<'single', string>
          type="single"
          {...workspaceSelectChipOverrides}
          value={dateFilter}
          onChange={onDateFilterChange}
          label={formatMessage(messages.date)}
          controlAccessibilityLabel={formatMessage(messages.filterByDate)}
          options={renderedDateOptions}
        />
      </Box>

      {hasActiveFilters ? (
        <Box flexShrink={0}>
          <Pressable
            onClick={handleClearFilters}
            background="transparent"
            noScaleOnPress
            paddingStart={2}
            accessibilityLabel={formatMessage(messages.clearFilters)}
          >
            <Text as="span" font="headline" color="fgPrimary" style={clearFiltersStyle}>
              {formatMessage(messages.clearFilters)}
            </Text>
          </Pressable>
        </Box>
      ) : null}
    </HStack>
  );
}
